import { useEffect } from 'react'
import { values as valuesFn, debounce } from '@axilon-apps/libs-shared-utils'
import {
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@axilon-apps/libs-ui-shadcn'
import { X } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'

import type { UsersFiltersFormValues } from '../../types/users-filters-form'

interface Props {
  onSubmit: (data: UsersFiltersFormValues) => void
  filters?: UsersFiltersFormValues
  showReset?: boolean
}

const defaultValues: UsersFiltersFormValues = {
  search: '',
  enabled: undefined,
  showServiceAccounts: false
}

const UsersFilterForm = ({ onSubmit, filters = defaultValues, showReset = true }: Props) => {
  const {
    control,
    handleSubmit,
    reset: resetForm,
    watch
  } = useForm<UsersFiltersFormValues>({ defaultValues: filters })

  const submitWithDebounce = debounce(() => {
    handleSubmit(onSubmit)()
  }, 500)

  const formValues = watch()

  useEffect(() => {
    submitWithDebounce()
    return () => submitWithDebounce.cancel()
  }, [...valuesFn(formValues)])

  const reset = () => resetForm()

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <Controller
            control={control}
            name="search"
            render={({ field }) => (
              <Field>
                <FieldLabel>Search</FieldLabel>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  type="search"
                  placeholder="Search by name, username, or email"
                />
              </Field>
            )}
          />
          <Controller
            control={control}
            name="enabled"
            render={({ field }) => (
              <Field>
                <FieldLabel>Status</FieldLabel>
                <Select
                  value={field.value === undefined ? 'all' : String(field.value)}
                  onValueChange={(value) =>
                    field.onChange(value === 'all' ? undefined : value === 'true')
                  }
                >
                  <SelectTrigger className="w-full">
                    <span className="flex min-w-0 flex-1 items-center justify-between">
                      <SelectValue />
                      {field.value !== undefined && (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="Clear selection"
                          onPointerDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            e.nativeEvent.stopImmediatePropagation()
                            field.onChange(undefined)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              e.stopPropagation()
                              field.onChange(undefined)
                            }
                          }}
                          className="grid size-4 cursor-pointer place-items-center rounded-sm opacity-50 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <X className="size-3.5" />
                        </span>
                      )}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          />
          <Controller
            control={control}
            name="showServiceAccounts"
            render={({ field }) => (
              <Field className="justify-end">
                <label className="flex h-9 cursor-pointer items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                  Show service accounts
                </label>
              </Field>
            )}
          />
        </div>
        {showReset && (
          <div className="flex flex-row justify-end">
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>
        )}
      </div>
    </form>
  )
}

export default UsersFilterForm
